import { memo } from 'react';

export enum FloatAnimationVariant {
  GentleFloat = 'gentleFloat',
  SlowDrift = 'slowDrift',
  FastBob = 'fastBob',
  WideSwing = 'wideSwing',
  SubtlePulse = 'subtlePulse',
  LazyFloat = 'lazyFloat',
  Bounce = 'bounce',
  LoopDeLoop = 'loopDeLoop',
  Updraft = 'updraft',
  Downwash = 'downwash',
  SideSlip = 'sideSlip',
  Wander = 'wander',
  HoverJitter = 'hoverJitter',
  Turbulence = 'turbulence',
  BigCircle = 'bigCircle',
  FigureEightWide = 'figureEightWide',
  Swoop = 'swoop',
  Glide = 'glide',
  MicroDrift = 'microDrift',
  CrystalDrift = 'crystalDrift',
  SunDrift = 'sunDrift',
  MoonDrift = 'moonDrift',
}

interface AnimationConfig {
  path: string;
  dur: string;
}

const ANIMATION_CONFIGS: Record<FloatAnimationVariant, AnimationConfig> = {
  [FloatAnimationVariant.GentleFloat]: {
    path: 'M0,0 Q-3,2 0,1.5 Q2,-1.5 0,0',
    dur: '5s',
  },
  [FloatAnimationVariant.SlowDrift]: {
    path: 'M0,0 C2,1 2,-1 0,-1.5 C-2,-1 -2,1 0,0',
    dur: '6s',
  },
  [FloatAnimationVariant.FastBob]: {
    path: 'M0,0 Q0,-2 0,-1 Q0,2 0,0',
    dur: '3s',
  },
  [FloatAnimationVariant.WideSwing]: {
    path: 'M0,0 Q-5,1 -4,0 Q0,-1 4,0 Q5,1 0,0',
    dur: '7s',
  },
  [FloatAnimationVariant.SubtlePulse]: {
    path: 'M0,0 Q-1,0.5 0,0.5 Q1,-0.5 0,0',
    dur: '8s',
  },
  [FloatAnimationVariant.LazyFloat]: {
    path: 'M0,0 C-2,3 2,3 0,2 C2,-2 -2,-2 0,0',
    dur: '10s',
  },
  [FloatAnimationVariant.Bounce]: {
    path: 'M0,0 Q0,-3 0,-2 Q0,1 0,0',
    dur: '4s',
  },

  // New configs (cloud-like motion: slow, smooth, drifting, occasional wobble)
  [FloatAnimationVariant.LoopDeLoop]: {
    // small loop that returns to origin (closed-ish)
    path: 'M0,0 C3,-2 6,2 3,4 C1,6 -3,5 -4,2 C-5,-1 -2,-2 0,0',
    dur: '9s',
  },
  [FloatAnimationVariant.Updraft]: {
    // rise, slight sway, settle
    path: 'M0,0 C-1,-1.5 1,-3 0,-4 C1,-3 2,-1 0,0',
    dur: '7s',
  },
  [FloatAnimationVariant.Downwash]: {
    // gentle sink with a side drift
    path: 'M0,0 C2,0.5 3,2 2,3.5 C1,5 -1,5 -2,3.5 C-3,2 -2,0.5 0,0',
    dur: '7s',
  },
  [FloatAnimationVariant.SideSlip]: {
    // horizontal slip with slight vertical wobble
    path: 'M0,0 C4,0.5 6,-0.5 8,0 C6,0.5 4,1.2 0,0 C-4,-1.2 -6,-0.5 -8,0 C-6,0.5 -4,0.5 0,0',
    dur: '11s',
  },
  [FloatAnimationVariant.Wander]: {
    // irregular wandering path (still smooth)
    path: 'M0,0 C2,-1 4,1 5,0 C6,-1 4,-3 2,-2 C0,-1 -2,1 -4,0 C-6,-1 -4,2 -2,2 C0,2 1,1 0,0',
    dur: '13s',
  },
  [FloatAnimationVariant.HoverJitter]: {
    // tiny jitter (good for background parallax feel)
    path: 'M0,0 Q0.6,-0.4 1.2,0 Q0.6,0.4 0,0.8 Q-0.6,0.4 -1.2,0 Q-0.6,-0.4 0,0',
    dur: '4.5s',
  },
  [FloatAnimationVariant.Turbulence]: {
    // "windy" wobble: mixed small waves
    path: 'M0,0 C2,-1 3,1 5,0 C3,1 2,2 0,1 C-2,0 -3,-1 -5,0 C-3,-1 -2,-2 0,0',
    dur: '6.5s',
  },
  [FloatAnimationVariant.BigCircle]: {
    // wide, slow circle (foreground hero cloud)
    path: 'M0,0 C6,0 6,6 0,6 C-6,6 -6,0 0,0',
    dur: '18s',
  },
  [FloatAnimationVariant.FigureEightWide]: {
    // wide figure-8
    path: 'M0,0 C5,-3 10,0 5,3 C0,6 -5,3 0,0 C5,-3 -5,-6 -5,-3 C-5,0 -2,1 0,0',
    dur: '14s',
  },
  [FloatAnimationVariant.Swoop]: {
    // dip and recover, like passing gust
    path: 'M0,0 C4,0 6,2 7,4 C6,1 3,-2 0,-1 C-3,0 -6,2 -7,4 C-6,2 -4,0 0,0',
    dur: '10s',
  },
  [FloatAnimationVariant.Glide]: {
    // gentle diagonal glide and return
    path: 'M0,0 C3,-1 6,-2 8,-1 C6,0 3,1 0,2 C-3,1 -6,0 -8,-1 C-6,-2 -3,-1 0,0',
    dur: '12s',
  },
  [FloatAnimationVariant.MicroDrift]: {
    // very subtle background drift
    path: 'M0,0 Q1,0.2 2,0 Q1,-0.2 0,-0.4 Q-1,-0.2 -2,0 Q-1,0.2 0,0',
    dur: '16s',
  },
  [FloatAnimationVariant.CrystalDrift]: {
    // snowflake motion: gentle hexagonal-inspired drift with delicate floating
    path: 'M0,0 C1.5,-0.8 2,0.5 1,1.5 C0,2.5 -1.5,1.5 -2,0.5 C-2.5,-0.5 -1,-1.5 0.5,-1 C1.5,-0.5 1,0.5 0,0',
    dur: '11s',
  },
  [FloatAnimationVariant.MoonDrift]: {
    // moon motion: gentle upward arch with very subtle sway (celestial feel)
    path: 'M0,0 C0.5,-1.5 1,-2 0,-2.5 C-1,-2 -0.5,-1.5 0,0',
    dur: '8s',
  },
  [FloatAnimationVariant.SunDrift]: {
    // sun motion: horizontal arc from left to right and back with upward curve
    path: 'M0,0 C-1,-1 -2.5,-1.5 -3,-1 C-2.5,-0.5 -1,0 0,-0.5 C1,-1 2.5,-1.5 3,-1 C2.5,-0.5 1,0 0,0',
    dur: '10s',
  },
};

const KEY_SPLINES = '0.45 0 0.55 1; 0.45 0 0.55 1';

interface FloatAnimationProps {
  variant?: FloatAnimationVariant;
  /** Enable gentle rotation/shake effect (ideal for snowflakes) */
  rotate?: boolean;
  /** Rotation amplitude in degrees (default: 15) */
  rotateAmplitude?: number;
  /** Rotation duration in seconds (default: 4) */
  rotateDuration?: number;
  /** Center X coordinate for rotation (required when rotate=true) */
  rotateCenterX?: number;
  /** Center Y coordinate for rotation (required when rotate=true) */
  rotateCenterY?: number;
  /** Enable zoom/pulse effect */
  zoom?: boolean;
  /** Minimum scale value (default: 0.92) */
  zoomMin?: number;
  /** Maximum scale value (default: 1.08) */
  zoomMax?: number;
  /** Zoom duration in seconds (default: 3) */
  zoomDuration?: number;
}

function FloatAnimation({
  variant = FloatAnimationVariant.GentleFloat,
  rotate = false,
  rotateAmplitude = 15,
  rotateDuration = 4,
  rotateCenterX = 0,
  rotateCenterY = 0,
  zoom = false,
  zoomMin = 0.92,
  zoomMax = 1.08,
  zoomDuration = 3,
}: FloatAnimationProps) {
  const { path, dur } = ANIMATION_CONFIGS[variant];

  // Calculate translate offset for center-based scaling
  const translateForZoom = zoom
    ? {
        // Translate to center, scale, translate back
        offsetX: rotateCenterX * (1 - zoomMin),
        offsetY: rotateCenterY * (1 - zoomMin),
        offsetXMax: rotateCenterX * (1 - zoomMax),
        offsetYMax: rotateCenterY * (1 - zoomMax),
      }
    : null;

  return (
    <>
      <animateMotion
        path={path}
        dur={dur}
        repeatCount="indefinite"
        calcMode="spline"
        keySplines={KEY_SPLINES}
      />
      {rotate && (
        <animateTransform
          attributeName="transform"
          type="rotate"
          values={`0 ${rotateCenterX} ${rotateCenterY}; ${rotateAmplitude} ${rotateCenterX} ${rotateCenterY}; 0 ${rotateCenterX} ${rotateCenterY}; ${-rotateAmplitude} ${rotateCenterX} ${rotateCenterY}; 0 ${rotateCenterX} ${rotateCenterY}`}
          dur={`${rotateDuration}s`}
          repeatCount="indefinite"
          calcMode="spline"
          keySplines="0.45 0 0.55 1; 0.45 0 0.55 1; 0.45 0 0.55 1; 0.45 0 0.55 1"
          additive="sum"
        />
      )}
      {zoom && translateForZoom && (
        <>
          <animateTransform
            attributeName="transform"
            type="translate"
            values={`0 0; ${translateForZoom.offsetXMax} ${translateForZoom.offsetYMax}; 0 0; ${translateForZoom.offsetX} ${translateForZoom.offsetY}; 0 0`}
            dur={`${zoomDuration}s`}
            repeatCount="indefinite"
            calcMode="spline"
            keySplines="0.45 0 0.55 1; 0.45 0 0.55 1; 0.45 0 0.55 1; 0.45 0 0.55 1"
            additive="sum"
          />
          <animateTransform
            attributeName="transform"
            type="scale"
            values={`1; ${zoomMax}; 1; ${zoomMin}; 1`}
            dur={`${zoomDuration}s`}
            repeatCount="indefinite"
            calcMode="spline"
            keySplines="0.45 0 0.55 1; 0.45 0 0.55 1; 0.45 0 0.55 1; 0.45 0 0.55 1"
            additive="sum"
          />
        </>
      )}
    </>
  );
}

export default memo(FloatAnimation);
